import React, { useState } from 'react';
import { ControllerFieldState } from 'react-hook-form';
import { InferBaseHtmlType, cn, triggerInputChange } from '@/components/ui/utils';
import useResizeObserver from 'use-resize-observer';
import { TappIcon } from '../TappIcon';
import { TappInputError } from '../TappInputError';

export type TappInputBaseType = Omit<React.ComponentProps<'input'>, 'prefix' | 'ref'>;
export type TappInputProps = {
  name?: string;
  border?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  inputClassName?: string;
  clearable?: boolean;
  onClear?: () => any;
} & TappInputBaseType &
  Partial<ControllerFieldState>;

const TappInput = React.forwardRef<InferBaseHtmlType<TappInputBaseType>, TappInputProps>(
  (
    {
      type,
      prefix,
      suffix,
      className,
      inputClassName,
      border = true,
      style,
      clearable,
      error,
      invalid,
      isDirty,
      isTouched,
      onClear,
      onInput,
      ...props
    },
    forwardedRef
  ) => {
    const internalRef = React.useRef<HTMLInputElement>(null);
    const [hasValue, setHasValue] = useState(false);

    const hasErrorVisible = invalid && error;

    const { ref: prefixRef, width: prefixWidth = 0 } = useResizeObserver<HTMLSpanElement>({ box: 'border-box' });
    const { ref: suffixRef, width: suffixWidth = 0 } = useResizeObserver<HTMLSpanElement>({ box: 'border-box' });

    const handleInput: React.FormEventHandler<HTMLInputElement> = (e) => {
      const val = e.currentTarget.value ?? '';
      setHasValue(val != null && val !== '');
      onInput?.(e);
    };

    React.useEffect(() => {
      if (internalRef.current) {
        const val = internalRef.current.value;
        setHasValue(val != null && val !== '');
      }
    }, [internalRef.current, props.value]);

    React.useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
      forwardedRef,
      () => internalRef.current
    );

    const clear: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (internalRef.current) {
        triggerInputChange(internalRef.current, '');
        // TODO: Check how to lose focus on the element, blur doesn't seem to work
        internalRef.current?.blur();
      }
      onClear?.();
    };

    return (
      <>
        <div className={cn('group relative', className)}>
          {!!prefix && (
            <span
              className={cn(
                'pointer-events-none absolute bottom-0 left-0 top-0 flex h-full items-center justify-center pl-4 pr-2',
                {
                  'text-destructive': hasErrorVisible,
                  'text-muted-foreground group-focus-within:text-primary group-[.has-focus]:text-primary':
                    !hasErrorVisible,
                }
              )}
              ref={prefixRef}
            >
              {prefix}
            </span>
          )}
          <input
            className={cn(
              'h-10 w-full rounded border bg-background px-4 text-start text-sm font-medium text-foreground transition placeholder:text-muted-foreground hover:bg-accent group-focus-within:bg-background group-[.has-focus]:bg-background',
              {
                'border-border': border && !hasErrorVisible,
                'border-transparent': !border && !hasErrorVisible,
                'border-destructive': hasErrorVisible,
                'group-focus-within:border-primary group-[.has-focus]:border-primary': !hasErrorVisible,
              },
              inputClassName
            )}
            style={{
              ...(prefixWidth > 0 && { paddingLeft: `${prefixWidth}px` }),
              ...(suffixWidth > 0 && { paddingRight: `${suffixWidth}px` }),
              ...(style ?? {}),
            }}
            ref={internalRef}
            onInput={handleInput}
            type={type !== 'button' ? type : 'text'}
            {...props}
            id={props.id || props.name}
          />

          <span
            ref={suffixRef}
            className={cn(
              'absolute bottom-0 right-0 top-0 flex h-full items-center justify-center gap-3 pl-2 pr-4 text-sm'
            )}
          >
            {!!clearable && hasValue && (
              <button className={cn('flex h-full items-center justify-center')} onClick={(e) => clear(e)}>
                <TappIcon size={16} className={cn('text-muted-foreground transition hover:text-secondary-foreground')}>
                  clear
                </TappIcon>
              </button>
            )}
            {!!suffix && (
              <span className={cn('flex h-full items-center justify-center text-muted-foreground')}>{suffix}</span>
            )}
          </span>
        </div>

        <TappInputError>{hasErrorVisible && error.message}</TappInputError>
      </>
    );
  }
);

TappInput.displayName = 'TappInput';
export { TappInput };
